/**
 * Mystery-shopping reports index. Scoped to `survey_type: mystery_shopping`
 * via the backend `/api/reports` endpoint — VOX / CSAT / NPS / eVOX data is
 * NEVER returned here. Filters (date range + branch) are sent as query
 * params and honored by the export endpoint too.
 *
 * Layout: tenant roles get the full DashboardLayout sidebar; the `client`
 * role gets a minimal own-header chrome (legacy behaviour preserved).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import useTenantFeatures from '@/hooks/useTenantFeatures';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { FileText, LogOut, Download, RefreshCw, ChevronDown, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { resolveComparisonIds, resolveAnswer } from '@/components/ComparisonHeader';
import { KpiCardRow } from '@/components/mystery_shopping/KpiCardRow';
import { PerformanceTrend } from '@/components/mystery_shopping/PerformanceTrend';
import { BranchRanking } from '@/components/mystery_shopping/BranchRanking';
import { FiltersBar, presetRange } from '@/components/mystery_shopping/FiltersBar';
import { SortHeader as SharedSortHeader } from '@/components/SortHeader';

function buildComparisonLabel(report) {
  const ids = resolveComparisonIds(report?.answers, report?.survey_config);
  if (!ids) return null;
  const r1Brand  = resolveAnswer(report.answers, report.qa_edits, ids.r1_brand);
  const r1Branch = resolveAnswer(report.answers, report.qa_edits, ids.r1_branch);
  const r2Brand  = resolveAnswer(report.answers, report.qa_edits, ids.r2_brand);
  const r2Branch = resolveAnswer(report.answers, report.qa_edits, ids.r2_branch);
  const left  = [r1Brand, r1Branch].filter(Boolean).join(' · ');
  const right = [r2Brand, r2Branch].filter(Boolean).join(' · ');
  if (!left && !right) return null;
  return { left, right };
}

function fmtDate(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return s; }
}

function SortHeader(props) {
  // Re-export so existing test IDs (reports-sort-*) remain unchanged.
  return <SharedSortHeader {...props} testIdPrefix="reports-sort" />;
}

function classificationPill(classification) {
  // Feb 2026 fix: pill reads the effective `classification` field returned
  // by /api/reports, which prioritises survey.is_comparison over the
  // attached branch's classification. Comparison FINDs are always
  // labelled Comparison even when their admin branch is client/competitor.
  if (classification === 'client')     return { text: 'Client',       cls: 'bg-[#1A3E6F]/10 text-[#1A3E6F]' };
  if (classification === 'competitor') return { text: 'Competitor',   cls: 'bg-[#D62B27]/10 text-[#D62B27]' };
  if (classification === 'comparison') return { text: 'Comparison',   cls: 'bg-[#64748B]/10 text-[#64748B]' };
  return                                       { text: 'Unclassified', cls: 'bg-[#FEF3C7] text-[#92400E]' };
}

function ReportsTable({ reports, onOpen, sortState, onSortClick }) {
  if (reports.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[#CBD5E1] rounded-xl p-12 text-center" data-testid="reports-empty">
        <FileText className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
        <p className="text-sm text-[#64748B]">No published reports yet.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#F8FAFC]">
            <SortHeader label="FIND ID" sortKey="find_id"  sortState={sortState} onSortClick={onSortClick} className="w-[120px]" />
            <SortHeader label="Branch"  sortKey="branch"   sortState={sortState} onSortClick={onSortClick} />
            <TableHead className="w-[120px]">Type</TableHead>
            <SortHeader label="Survey"  sortKey="survey"   sortState={sortState} onSortClick={onSortClick} />
            <SortHeader label="Visit date" sortKey="visit_date" sortState={sortState} onSortClick={onSortClick} className="w-[120px]" />
            <SortHeader label="TrustX®" sortKey="trustx"   sortState={sortState} onSortClick={onSortClick} className="w-[110px]" />
            <TableHead className="w-[90px]">NPS®</TableHead>
            <TableHead className="w-[120px] text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reports.map(r => {
            const comparison = buildComparisonLabel(r);
            const trustx = (r.trustx_overall == null) ? '—' : `${r.trustx_overall}%`;
            // Feb 2026 refinement — NPS® column. Row's `nps` field is
            // the raw 0..10 rating (single response) — we display it
            // with a sign, no percent (e.g. "+9", "—" when absent).
            const npsRaw = (r.nps == null) ? null : Number(r.nps);
            const npsLabel = (npsRaw == null || Number.isNaN(npsRaw))
              ? '—'
              : (npsRaw > 0 ? `+${npsRaw}` : `${npsRaw}`);
            const pill = classificationPill(r.classification);
            return (
              <TableRow key={r.response_id} data-testid={`report-row-${r.response_id}`} className="hover:bg-[#F8FAFC]">
                <TableCell className="font-mono text-xs text-[#475569]">{r.public_id || r.response_id}</TableCell>
                <TableCell>
                  {comparison ? (
                    <span data-testid={`report-row-comparison-${r.response_id}`} className="text-sm">
                      <span className="font-medium text-[#0F172A]">{comparison.left || '—'}</span>
                      <span className="mx-2 text-xs text-[#94A3B8]">vs</span>
                      <span className="font-medium text-[#0F172A]">{comparison.right || '—'}</span>
                      <Badge className="ml-2 bg-amber-50 text-amber-800 border border-amber-200 text-[10px]" data-testid={`report-row-comparison-badge-${r.response_id}`}>Comparison</Badge>
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-[#0F172A]">{r.branch_name || '—'}</span>
                  )}
                </TableCell>
                <TableCell data-testid={`report-row-classification-${r.response_id}`}>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${pill.cls}`}>
                    {pill.text}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-[#475569]">{r.survey_name || '—'}</TableCell>
                <TableCell className="text-sm text-[#475569]">{fmtDate(r.visit_date)}</TableCell>
                <TableCell className="text-sm font-semibold text-[#0F172A]" data-testid={`report-row-trustx-${r.response_id}`}>{trustx}</TableCell>
                <TableCell className="text-sm font-semibold text-[#0F172A]" data-testid={`report-row-nps-${r.response_id}`}>{npsLabel}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onOpen(r.response_id)}
                    data-testid={`report-row-view-${r.response_id}`}
                  >
                    View Report
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function ReportsBody() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const features = useTenantFeatures();
  // Reduced Phase 1 gate: BOTH flags required.
  const showNewDashboard = !!(features?.products_mystery_shopping && features?.ms_new_dashboard);
  const [reports, setReports] = useState([]);
  const [branches, setBranches] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Default date range: "All Time" (Step 2 — Feb 2026). Reports load
  // immediately without requiring the user to pick a preset first.
  const [range, setRange] = useState(() => presetRange('all_time'));
  const [branchType, setBranchType] = useState('all');           // all | client | competitor
  const [selectedBranchIds, setSelectedBranchIds] = useState([]); // strings (real ids + comp: tokens)
  // Column sort: default visit_date DESC per spec. First click on a header
  // toggles to ASC → DESC → ASC …
  const [sortState, setSortState] = useState({ key: 'visit_date', dir: 'desc' });
  const onSortClick = (key) => {
    setSortState(prev => (
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    ));
  };
  const isClient = user?.role === 'client';

  // Reduced Phase 1 — set of competitor branch ids, used by trend/ranking to
  // tint / bucket rows without another network call.
  const competitorBranchIds = useMemo(
    () => branches.filter(b => b.branch_type === 'competitor').map(b => b.branch_id),
    [branches],
  );

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (range.from) p.set('date_from', range.from);
    if (range.to)   p.set('date_to', range.to);
    if (branchType && branchType !== 'all') p.set('branch_type', branchType);
    if (selectedBranchIds.length > 0) p.set('branch_ids', selectedBranchIds.join(','));
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [range, branchType, selectedBranchIds]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/reports${queryString}`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) { toast.error(d?.detail || 'Could not load reports'); setReports([]); return; }
      setReports(d.reports || []);
    } catch { toast.error('Could not load reports'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [queryString]);

  // Reduced Phase 1 — fetch KPI feed alongside the reports list. Feature-
  // flag gated; silently ignored when the endpoint isn't available yet.
  useEffect(() => {
    if (!showNewDashboard) { setKpis(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/reports/dashboard-kpis${queryString}`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setKpis(d);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [queryString, showNewDashboard]);

  // Re-fetch branch list whenever the type filter changes so the
  // multi-select reflects only matching branches per the spec.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = branchType && branchType !== 'all' ? `?branch_type=${branchType}` : '';
        const res = await fetch(`${API}/reports/branches${qs}`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setBranches(d.branches || []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [branchType]);

  // When the type filter narrows, prune any selected branches that no longer
  // belong to the visible set so the chip-count never lies.
  useEffect(() => {
    if (selectedBranchIds.length === 0) return;
    const visibleSet = new Set(branches.map(b => b.branch_id));
    const pruned = selectedBranchIds.filter(id => visibleSet.has(id));
    if (pruned.length !== selectedBranchIds.length) {
      setSelectedBranchIds(pruned);
    }
  }, [branches, selectedBranchIds]);

  // Client-side sort — preserves current filter state (Aug 2026 spec).
  // Missing values always fall to the bottom regardless of direction.
  const sortedReports = useMemo(() => {
    const arr = reports.slice();
    const { key, dir } = sortState;
    const factor = dir === 'asc' ? 1 : -1;
    const numeric = (v) => (typeof v === 'number' && !Number.isNaN(v)) ? v : null;
    const text = (v) => (v == null ? null : String(v).toLowerCase());
    const getVal = (r) => {
      switch (key) {
        case 'find_id':    return text(r.public_id || r.response_id);
        case 'branch':     return text(r.branch_name);
        case 'survey':     return text(r.survey_name);
        case 'visit_date': return text(r.visit_date);
        case 'trustx':     return numeric(r.trustx_overall);
        case 'overall':    return numeric(r.overall_rating);
        default:           return null;
      }
    };
    arr.sort((a, b) => {
      const av = getVal(a); const bv = getVal(b);
      // Nulls last always — regardless of sort direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * factor;
      if (av > bv) return  1 * factor;
      // Tiebreaker: visit_date desc → then published_at desc.
      const av2 = a.visit_date || '';
      const bv2 = b.visit_date || '';
      if (av2 !== bv2) return av2 < bv2 ? 1 : -1;
      const ap = a.published_at || '';
      const bp = b.published_at || '';
      return ap < bp ? 1 : (ap > bp ? -1 : 0);
    });
    return arr;
  }, [reports, sortState]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/reports/export${queryString}`, { credentials: 'include' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d?.detail || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mystery-shopping-reports.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export ready');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const headerForClient = (
    <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-10">
      <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#1A3E6F]" />
          <span className="font-bold text-[#0F172A]">satisFIND<sup>®</sup> Reports</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#64748B] hidden sm:inline">{user?.email}</span>
          <Button size="sm" variant="ghost" onClick={() => { logout(); navigate('/auth'); }} data-testid="reports-logout">
            <LogOut className="w-4 h-4 mr-1" /> Sign out
          </Button>
        </div>
      </div>
    </header>
  );

  const body = (
    <main className="max-w-6xl mx-auto px-5 py-8 space-y-5" data-testid="reports-list-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Published Reports</h1>
          <p className="text-sm text-[#64748B] mt-0.5">{isClient ? 'Click any report to view it.' : 'Mystery shopping reports published to your tenant.'}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="reports-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <FiltersBar
        range={range}
        onRangeChange={setRange}
        branchType={branchType}
        onBranchTypeChange={(t) => { setBranchType(t); }}
        selectedBranchIds={selectedBranchIds}
        onSelectedBranchIdsChange={setSelectedBranchIds}
        branches={branches}
        onExport={handleExport}
        exporting={exporting}
      />

      {showNewDashboard && (
        <div className="space-y-4" data-testid="ms-new-dashboard">
          {kpis && <KpiCardRow kpis={kpis} />}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PerformanceTrend reports={reports} competitorBranchIds={competitorBranchIds} />
            <BranchRanking reports={reports} competitorBranchIds={competitorBranchIds} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-[#64748B]" data-testid="reports-loading">Loading reports…</div>
      ) : (
        <ReportsTable
          reports={sortedReports}
          onOpen={(id) => navigate(`/reports/${id}`)}
          sortState={sortState}
          onSortClick={onSortClick}
        />
      )}
    </main>
  );

  if (isClient) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        {headerForClient}
        {body}
      </div>
    );
  }
  return <DashboardLayout>{body}</DashboardLayout>;
}

export default function ReportsList() {
  return <ReportsBody />;
}
